import { useEffect, useMemo, useState } from "react"

import { formatNumber } from "accounting"
import { Spin } from "antd"
import { ArcElement, Chart as ChartJS, Tooltip as ChartTooltip, Legend } from "chart.js"
import { Doughnut } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { INCOGNITO_MASK } from "../../../../Components/Private"
import { selectIncognito } from "../../../../Utils/incognito"
import { formatCurrency } from "../../../../Utils/utils"
import { roundPct } from "../../../DashboardPage/components/Sankey"
import { DEFLECT_SECTION } from "../../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../../redux/deflectAnalytics.slice"
import { CHANNEL_COLORS, channelLabel } from "../../utils/vocabulary"
import { reasonLegendPlugin } from "../reasonLegendPlugin"
import Section from "../Section"
import Toggle from "../Toggle"
import ChannelDonutSkeleton from "./ChannelDonutSkeleton"

import styles from "./channelDonut.module.less"

ChartJS.register(ArcElement, ChartTooltip, Legend)

const LEGEND_ID = "legend-container-lookups-by-channel"

const CHANNEL_ORDER = Object.keys(CHANNEL_COLORS)

// Prevent's "Alerts by Source" donut tooltip: the dark #27272A HTML box. Lines come
// from tooltip.callbacks.title (0 = heading, 1 = count + value with the gray-circle
// separator). Same colours as Prevent (kept verbatim for parity; a canvas/HTML
// tooltip can't read Less tokens). Uses a dedicated tooltip element id (like the
// country/trend charts) so its wider 236px box can't leave stale width on the
// trend chart's tooltip; still gets the shared #chartjs-tooltip* CSS + cleanup.
const TOOLTIP_ID = "chartjs-tooltip_deflect_donut"
const externalDonutTooltip = (context) => {
  let tooltipEl = document.getElementById(TOOLTIP_ID)
  if (!tooltipEl) {
    tooltipEl = document.createElement("div")
    tooltipEl.id = TOOLTIP_ID
    tooltipEl.innerHTML = '<table style="width: 100%;"></table>'
    document.body.appendChild(tooltipEl)
  }
  const tooltipModel = context.tooltip
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0
    return
  }
  tooltipEl.classList.remove("above", "below", "no-transform")
  tooltipEl.classList.add(tooltipModel.yAlign || "no-transform")
  tooltipEl.style.zIndex = "1010"
  tooltipEl.style.backgroundColor = "#27272A"
  tooltipEl.style.borderRadius = "12px"
  tooltipEl.style.padding = "12px 12px"
  tooltipEl.style.color = "white"
  tooltipEl.style.width = "236px"
  if (tooltipModel.body) {
    const titleLines = tooltipModel.title || []
    let innerHtml = "<thead>"
    const styleTitle = "padding-bottom: 0px; text-align: start; font-weight: 500; color: #7A7A83"
    const styleTitle2 = "padding: 0px; padding-top: 12px; font-weight: 400; text-align: start"
    titleLines.forEach((value, i) => {
      if (i === 0) innerHtml += '<tr><th style="' + styleTitle + '">' + value + "</th></tr>"
      else if (value) innerHtml += '<tr><th style="' + styleTitle2 + '">' + value + "</th></tr>"
    })
    innerHtml += "</thead><tbody>"
    tooltipEl.querySelector("table").innerHTML = innerHtml
  }
  const position = context.chart.canvas.getBoundingClientRect()
  // Glide between slices while already visible (fade in place on first show, see chartsOptions).
  const wasVisible = parseFloat(tooltipEl.style.opacity || 0) > 0
  tooltipEl.style.transition = wasVisible
    ? "left 0.13s cubic-bezier(0.22, 1, 0.36, 1), top 0.13s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.12s ease"
    : "opacity 0.12s ease"
  tooltipEl.style.opacity = 1
  tooltipEl.style.position = "absolute"
  // Kept inside the viewport, as Prevent's chart tooltips are: on a phone a slice near the
  // right edge would otherwise push the box off screen.
  const left = position.left - 77 + window.scrollX + tooltipModel.caretX
  tooltipEl.style.left =
    Math.max(
      window.scrollX,
      Math.min(left, window.scrollX + window.innerWidth - tooltipEl.offsetWidth)
    ) + "px"
  tooltipEl.style.top = position.top + 20 + window.scrollY + tooltipModel.caretY + "px"
  tooltipEl.style.padding = tooltipModel.padding + "px "
  tooltipEl.style.pointerEvents = "none"
}

// "Lookups by channel" = a copy of the Alerts by Source card: the same donut (74% cutout,
// rounded offset segments, centred total) + the same legend table (reasonLegendPlugin). The
// channel sets the volume charged, so "By volume" is the fees, not the order value.
const ChannelDonut = ({ getData }) => {
  const { t } = useTranslation()
  const theme = useSelector((s) => s.nav.theme)
  // Incognito masks money everywhere else on this page; mirror it in the donut centre,
  // tooltip and legend so the $ volume can't leak while the rest of the page is masked.
  const incognito = useSelector(selectIncognito)
  const byChannel = useSelector(selectDeflectSection(DEFLECT_SECTION.BY_CHANNEL))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.BY_CHANNEL))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.BY_CHANNEL))
  const [metric, setMetric] = useState("count")
  // Slices the user has clicked off in the legend (label -> hidden). Reset when
  // the count/volume toggle flips, since that remounts the chart (same as Alerts).
  const [filterData, setFilterData] = useState({})
  useEffect(() => setFilterData({}), [metric])

  useEffect(() => {
    getData(DEFLECT_SECTION.BY_CHANNEL)
  }, [getData])

  // The tooltip element lives on <body>, outside React, so the chart that creates it removes it.
  useEffect(() => () => document.getElementById(TOOLTIP_ID)?.remove(), [])

  // by-channel/ carries both metrics, so the toggle never asks again. Every channel keeps its row in one
  // fixed order whatever the toggle; a channel without lookups reads zero (RCA-8-02, RCA-8-03).
  const rows = useMemo(() => {
    const byKey = Object.fromEntries((byChannel?.result ?? []).map((row) => [row.channel, row]))

    return CHANNEL_ORDER.map((key) => ({
      key,
      label: t(channelLabel(key)),
      count: byKey[key]?.lookups_count ?? 0,
      value: byKey[key]?.fees_amount ?? 0
    }))
  }, [byChannel, t])
  const valueOf = (r) => (metric === "value" ? r.value : r.count)
  // Centre total excludes legend-hidden slices, matching Alerts by Source.
  const grandTotal = rows.reduce((acc, r) => acc + (filterData[r.label] ? 0 : valueOf(r)), 0)
  const fmtMetric = (v) =>
    metric === "value" ? formatCurrency(v, true) : formatNumber(v, 0, ",", ".")

  const textCenter = {
    id: "deflectDonutCenter",
    afterDatasetsDraw(chart) {
      const { ctx } = chart
      const meta = chart.getDatasetMeta(0)?.data?.[0]
      if (!meta) return
      ctx.save()
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.font = "400 24px/24px Inter"
      ctx.fillStyle = theme === "dark" ? "#FCFCFC" : "#101828"
      // Read the total off the chart: react-chartjs-2 keeps the plugins of the first render,
      // so a closed-over grandTotal would stay stale after a refetch or a legend click.
      const total = chart.data.datasets[0].total
      ctx.fillText(incognito ? INCOGNITO_MASK : fmtMetric(total), meta.x, meta.y - 10)
      ctx.font = "400 14px/20px Inter"
      ctx.fillStyle = "#7A7A83"
      ctx.fillText(metric === "value" ? t("Volume") : t("Lookups"), meta.x, meta.y + 12)
      ctx.restore()
    }
  }

  const data = {
    labels: rows.map((r) => r.label),
    datasets: [
      {
        data: rows.map(valueOf),
        backgroundColor: rows.map((r) => CHANNEL_COLORS[r.key]),
        borderColor: theme === "dark" ? "#141414" : "#FFFFFF",
        borderRadius: 6,
        hoverOffset: 10,
        hoverBorderColor: "transparent",
        offset: 4,
        cutout: "74%",
        total: grandTotal,
        count: rows.map((r) => r.count),
        volume: rows.map((r) => r.value)
      }
    ]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      htmlLegend: { containerID: LEGEND_ID },
      legend: { display: false },
      tooltip: {
        enabled: false,
        callbacks: {
          // line 0 = channel (muted heading); line 1 = count + value with the
          // gray-circle separator, mirroring Alerts by Source; line 2 = the slice's share of
          // the toggled metric, the same total the centre shows.
          title: (ctx) => {
            const r = rows[ctx?.[0]?.dataIndex]
            if (!r) return ["", ""]
            return [
              r.label,
              (incognito ? INCOGNITO_MASK : formatNumber(r.count, 0, ",")) +
                '<span class="gray-circle"></span>' +
                (incognito ? INCOGNITO_MASK : formatCurrency(r.value)),
              incognito
                ? INCOGNITO_MASK
                : t("_percent_% of total", { percent: roundPct(valueOf(r), grandTotal) })
            ]
          }
        },
        external: externalDonutTooltip
      }
    }
  }

  const toggle = (
    <Toggle
      value={metric}
      onChange={setMetric}
      options={[
        { value: "count", label: "By count" },
        { value: "value", label: "By volume" }
      ]}
    />
  )

  return (
    <Section
      title={t("Lookups by channel")}
      tooltip={t(
        "The channel the inquiry arrived on. It sets the volume charged: $0.50 banking app, $5.00 call center, $0.50 Mastercom."
      )}
      extra={toggle}
      minHeight={536}
    >
      {firstLoad ? (
        <ChannelDonutSkeleton />
      ) : (
        <Spin spinning={fetching}>
          <>
            <div className={styles.donut}>
              <Doughnut
                key={`${metric}-${theme}-${incognito}`}
                data={data}
                options={options}
                plugins={[reasonLegendPlugin(setFilterData, t, incognito), textCenter]}
              />
            </div>
            <div id={LEGEND_ID} className={styles.legend} />
          </>
        </Spin>
      )}
    </Section>
  )
}

export default ChannelDonut
